import { theme, ThemeConfig } from 'antd'

// Bitquery brand theme for AntD. Colour values mirror graphql-ide's
// src/scss/_brand.scss + src/scss/_themes.scss (the single source of truth for
// the brand palette), mapped onto AntD tokens. AntD's algorithm derives the rest
// of the scale (hover/active/disabled) from these, and cssVar emits them as CSS
// variables so a theme switch is a cheap variable swap.

// --- Brand palette (mirrors _brand.scss) -----------------------------------
export const BRAND = {
    magenta: '#93254b', // primary CTA / accent
    magentaBright: '#b42d5b', // hover / active
    magentaPink: '#e25284', // brighter pink (readable on dark)
    magentaDeep: '#621832', // pressed / active
    periwinkle: '#627eea', // brand blue — info accent (matches IDE's blue info)
} as const

// Theme-agnostic component tweaks (kept from the original config).
const baseComponents: ThemeConfig['components'] = {
    Divider: { marginLG: 0, margin: 0 },
    Tag: { marginXS: 4 },
}

const getComponents = (dark: boolean): ThemeConfig['components'] => ({
    ...baseComponents,
    Slider: {
        trackBg: BRAND.magenta,
        trackHoverBg: BRAND.magentaBright,
        handleColor: dark ? BRAND.magentaPink : BRAND.magenta,
        handleActiveColor: BRAND.magentaBright,
        dotActiveBorderColor: BRAND.magenta,
        ...(dark
            ? { railBg: 'rgba(255, 255, 255, 0.22)', railHoverBg: 'rgba(255, 255, 255, 0.32)' }
            : {}),
    },
})

// Light theme — :root / [data-theme='light'] in _themes.scss
const lightToken: ThemeConfig['token'] = {
    borderRadius: 6,
    colorPrimary: BRAND.magenta,
    colorPrimaryHover: BRAND.magentaBright,
    // Info accent (Alerts, processing tags) — brand blue, not generic AntD blue.
    colorInfo: BRAND.periwinkle,
    // Links/`<a>`/type="link" follow colorLink (NOT colorPrimary) — brand them too.
    colorLink: BRAND.magenta,
    colorLinkHover: BRAND.magentaBright,
    colorLinkActive: BRAND.magentaDeep,
    colorBgLayout: '#f6f7f8', // --bq-bg (page)
    colorBgContainer: '#ffffff', // --bq-surface (panels / cards / inputs)
    colorBgElevated: '#ffffff', // --bq-elevated (dropdowns / modals)
    colorBgSpotlight: '#1f1f1f', // tooltips — dark neutral like IDE (--bq-elevated), no blue tint
    colorText: '#24292e', // --bq-text
    colorTextSecondary: '#6e7781', // --bq-text-muted
    colorBorder: '#d0d7de', // --bq-border-strong
    colorBorderSecondary: '#e1e4e8', // --bq-border
}

// Dark theme — [data-theme='dark'] in _themes.scss (bitquery.io near-black)
const darkToken: ThemeConfig['token'] = {
    borderRadius: 6,
    colorPrimary: BRAND.magenta,
    colorPrimaryHover: BRAND.magentaBright,
    // Info accent (Alerts, processing tags) — brand blue, not generic AntD blue.
    colorInfo: BRAND.periwinkle,
    // Brighter magenta for links so they stay readable on the near-black surface.
    colorLink: BRAND.magentaBright,
    colorLinkHover: BRAND.magentaPink,
    colorLinkActive: BRAND.magenta,
    colorBgBase: '#06090d', // --bq-bg — algorithm derives the dark scale from this
    colorBgLayout: '#06090d', // page
    colorBgContainer: '#0b0f16', // --bq-surface (panels / cards / inputs)
    colorBgElevated: '#1f1f1f', // --bq-elevated (dropdowns / modals)
    colorBgSpotlight: '#1f1f1f', // tooltips — dark neutral like IDE (--bq-elevated), no blue tint
    colorText: '#c3cad6', // --bq-text
    colorTextSecondary: '#7e8696', // --bq-text-muted
    colorBorder: '#2b303a', // dim, near-neutral (was #3b4663 — read too grey-blue)
    colorBorderSecondary: '#1b1f2b', // --bq-border (dividers)
}

export const getAntdTheme = (dark: boolean): ThemeConfig => ({
    algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    cssVar: true,
    token: dark ? darkToken : lightToken,
    components: getComponents(dark),
})
